/* ══════════════════════════════════════════════════════════════════
   Der Höhen-Anstoß der installierten Web-App (07.10.2026, Signal-Umbau
   Paket 2, docs/64-designsystem-signal.md).

   **Was iOS 26 falsch macht.** Nach einem Laden **in** der Web-App (vom
   Startbildschirm, nicht im Safari-Reiter) meldet iOS `innerHeight` als
   Bildschirm minus oberen sicheren Bereich — auf dem iPhone 17 Pro 812
   statt 874 — und zeichnet auch nur bis dahin. Die untere Leiste steht
   dann 62 px über der Kante, darunter bleibt ein leerer Streifen. Ein
   Gummiband-Zug richtet es, eine CSS-Antwort gibt es nicht (gemessen mit
   `bottom`, `100lvh` und `100dvh`).

   **Was hilft:** das Dokument 80 ms um Fehlbetrag + 20 px rollen und
   zurück; der Körper fährt im selben Moment gegenläufig (`translateY`),
   damit nichts zuckt. Höchstens drei Versuche je Anlass — Laden,
   Zurückkehren aus dem Hintergrund, Drehen. Nie im Safari-Reiter, nie in
   einer nativen Hülle (dann fehlt `ist-standalone`, `index.html`).

   Übernommen aus app-design-muster 4.7.1 (`vorlagen/basis.js`,
   `mm.standaloneHoehe` und `mm.regeln.fehlendeHoehe`), dort aus OVIS
   (`client/src/utils/standaloneHoehe.js`, A-NEU-PWA-1). Am Gerät ist es
   auch dort noch nicht abgenommen — der Abschnitt „Abnahme am Gerät" in
   docs/64 sagt, wie es zu prüfen ist.
   ══════════════════════════════════════════════════════════════════ */

export type Fensterlage = {
  innenHoehe: number;
  innenBreite: number;
  schirmBreite: number;
  schirmHoehe: number;
  sicherOben: number;
};

/** **Die reine Regel:** Wie viel Höhe fehlt der Web-App — oder 0.
 *
 *  Gezählt wird **nur** genau der Fehlbetrag der Statusleiste (± 2 px). Ein
 *  geteilter Bildschirm auf dem iPad ist auch kürzer als der Schirm, aber nie
 *  um genau den oberen sicheren Bereich — und dort darf nichts angestoßen
 *  werden. Quer ist die Sollhöhe die kurze Seite des Schirms. */
export function fehlendeHoehe(l: Fensterlage): number {
  const zahlen = [l.innenHoehe, l.innenBreite, l.schirmBreite, l.schirmHoehe, l.sicherOben];
  if (zahlen.some((x) => !Number.isFinite(x)) || l.sicherOben <= 0) return 0;
  const soll = l.innenBreite > l.innenHoehe
    ? Math.min(l.schirmBreite, l.schirmHoehe)
    : Math.max(l.schirmBreite, l.schirmHoehe);
  const fehlt = soll - l.innenHoehe;
  return Math.abs(fehlt - l.sicherOben) <= 2 ? fehlt : 0;
}

const HALTEN_MS = 80;
const ZUGABE_PX = 20;
const VERSUCHE = 3;

/** Der obere sichere Bereich in Pixeln — `env()` lässt sich nur über ein
 *  gezeichnetes Element lesen. */
function sicherObenMessen(): number {
  const p = document.createElement("div");
  p.style.cssText = "position:absolute;visibility:hidden;pointer-events:none;height:env(safe-area-inset-top,0px)";
  document.body.append(p);
  const h = p.getBoundingClientRect().height;
  p.remove();
  return h;
}

function lage(): Fensterlage {
  return {
    innenHoehe: window.innerHeight,
    innenBreite: window.innerWidth,
    schirmBreite: window.screen?.width ?? Number.NaN,
    schirmHoehe: window.screen?.height ?? Number.NaN,
    sicherOben: sicherObenMessen(),
  };
}

let versuche = 0;
let laeuft = false;

function anstossen(): void {
  if (laeuft || !document.body) return;
  const fehlt = fehlendeHoehe(lage());
  if (!fehlt) { versuche = 0; return; }
  if (versuche >= VERSUCHE) return;
  versuche += 1;
  laeuft = true;
  const w = document.documentElement;
  const k = document.body;
  const weg = Math.ceil(fehlt) + ZUGABE_PX;
  const vorher = { minHeight: w.style.minHeight, transform: k.style.transform };
  w.style.minHeight = `calc(100% + ${weg * 2}px)`;
  k.style.transform = `translateY(${weg}px)`;
  window.scrollTo(0, weg);
  window.setTimeout(() => {
    window.scrollTo(0, 0);
    w.style.minHeight = vorher.minHeight;
    k.style.transform = vorher.transform;
    laeuft = false;
    if (fehlendeHoehe(lage())) window.setTimeout(anstossen, 250);
    else versuche = 0;
  }, HALTEN_MS);
}

function anlass(verzug = 0): () => void {
  return () => { versuche = 0; window.setTimeout(anstossen, verzug); };
}

/** Einmal beim Start (`main.tsx`). Außerhalb der installierten Web-App tut
 *  es nichts. */
export function standaloneHoeheEinrichten(): void {
  if (typeof document === "undefined") return;
  if (!document.documentElement.classList.contains("ist-standalone")) return;
  if (document.readyState === "complete") anlass()();
  else window.addEventListener("load", anlass(), { once: true });
  window.addEventListener("pageshow", anlass());
  document.addEventListener("visibilitychange", () => { if (!document.hidden) anlass()(); });
  // Im Ereignis meldet `innerHeight` noch das alte Maß.
  window.addEventListener("orientationchange", anlass(400));
}
